// src/pages/CommandsPage.jsx
//
// Pagina publica listando todos os comandos do plugin — dobra como catalogo
// de features (decisao de compra) e como pagina indexavel pro SEO (cada nome
// de comando e um termo de busca em potencial, tipo "RNPlugin exportar
// tabela Civil 3D").

import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { categorias, comandos, comandosOrdenados } from '../data/comandos';

const TODAS = 'todas';

const CommandsPage = () => {
  const [categoriaAtiva, setCategoriaAtiva] = useState(TODAS);
  const [busca, setBusca] = useState('');

  const comandosFiltrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return comandosOrdenados.filter((c) => {
      const bateCategoria = categoriaAtiva === TODAS || c.categoria === categoriaAtiva;
      const bateBusca =
        termo === '' ||
        c.nome.toLowerCase().includes(termo) ||
        c.descricao.toLowerCase().includes(termo);
      return bateCategoria && bateBusca;
    });
  }, [categoriaAtiva, busca]);

  const categoriaPorId = useMemo(
    () => Object.fromEntries(categorias.map((c) => [c.id, c])),
    []
  );

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'RNPlugin',
    applicationCategory: 'EngineeringApplication',
    operatingSystem: 'Windows',
    description:
      'Plugin para AutoCAD Civil 3D voltado a projetos de rodovia, ferrovia, loteamento e topografia: geometria, perfil, corredor, rótulos e exportação, do jeito que o projetista precisa.',
    featureList: comandos.map((c) => c.nome),
  };

  return (
    <div>
      <Helmet>
        <title>
          Comandos do RNPlugin | Automação para Rodovia, Ferrovia e Loteamento no Civil 3D
        </title>
        <meta
          name="description"
          content="Todos os comandos do RNPlugin para quem projeta rodovia, ferrovia, loteamento e topografia no Civil 3D: geometria, perfil, corredor, rótulos, exportação e Street View integrado."
        />
        <link rel="canonical" href="https://rnplugin.com.br/comandos" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Header */}
      <section className="on-navy bg-rn-navy bg-pattern py-16 lg:py-20 text-center text-white">
        <div className="container mx-auto px-6">
          <h1 className="text-[32px] md:text-[48px] font-semibold leading-[1.15] mb-4">
            Os comandos do{' '}
            <span className="text-rn-orange">RNPlugin</span> para rodovia, ferrovia,
            loteamento e topografia
          </h1>
          <p className="text-lg text-rn-text-soft-dk max-w-2xl mx-auto mb-8">
            {comandos.length} comandos pensados para a rotina de quem projeta
            rodovia, ferrovia, loteamento e topografia no Civil 3D — geometria,
            perfil, corredor, terraplenagem, exportação e mapas. Nada de
            ferramenta genérica de desenho.
          </p>

          <div className="max-w-lg mx-auto">
            <input
              type="search"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar comando (ex.: perfil, corredor, exportar...)"
              aria-label="Buscar comando"
              className="w-full rounded px-6 py-3 text-rn-navy placeholder-rn-text-soft focus:outline-none focus:ring-4 focus:ring-rn-orange-light/40"
            />
          </div>
        </div>
      </section>

      {/* Filtro de categorias */}
      <section className="bg-rn-surface border-b border-rn-line sticky top-16 z-30">
        <div className="container mx-auto px-6 py-4 overflow-x-auto">
          <div className="flex gap-2 w-max">
            <button
              onClick={() => setCategoriaAtiva(TODAS)}
              className={`px-4 py-2 rounded text-sm font-semibold transition-colors whitespace-nowrap ${
                categoriaAtiva === TODAS
                  ? 'bg-rn-navy text-white'
                  : 'bg-rn-bg text-rn-text-soft hover:bg-rn-line'
              }`}
            >
              Todas ({comandos.length})
            </button>
            {categorias.map((cat) => {
              const total = comandos.filter((c) => c.categoria === cat.id).length;
              if (total === 0) return null;
              return (
                <button
                  key={cat.id}
                  onClick={() => setCategoriaAtiva(cat.id)}
                  className={`px-4 py-2 rounded text-sm font-semibold transition-colors whitespace-nowrap ${
                    categoriaAtiva === cat.id
                      ? 'bg-rn-navy text-white'
                      : 'bg-rn-bg text-rn-text-soft hover:bg-rn-line'
                  }`}
                >
                  {cat.emoji} {cat.nome} ({total})
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Grid de comandos */}
      <section className="py-12 lg:py-16 bg-rn-bg">
        <div className="container mx-auto px-6">
          {comandosFiltrados.length === 0 ? (
            <p className="text-center text-rn-text-soft py-16">
              Nenhum comando encontrado para "{busca}".
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {comandosFiltrados.map((cmd) => {
                const cat = categoriaPorId[cmd.categoria];
                return (
                  <Card key={cmd.nome} className="p-6 flex flex-col relative">
                    {cmd.status === 'novo' && (
                      <span className="absolute top-4 right-4 bg-rn-orange text-rn-navy text-xs font-semibold px-2 py-1 rounded-full">
                        Novo
                      </span>
                    )}
                    <div className="text-2xl mb-3" aria-hidden="true">
                      {cat?.emoji}
                    </div>
                    <h2 className="text-lg font-mono font-semibold text-rn-navy mb-2 pr-14">
                      {cmd.nome}
                    </h2>
                    <p className="text-rn-text-soft text-sm flex-grow">
                      {cmd.descricao}
                    </p>
                    <span className="mt-4 text-xs font-semibold text-rn-text-soft uppercase tracking-wide">
                      {cat?.nome}
                    </span>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA final */}
      <section className="on-navy bg-rn-navy py-16 text-center text-white">
        <div className="container mx-auto px-6">
          <h2 className="text-[27px] md:text-[32px] font-semibold mb-4">
            Todos esses comandos, em um plano só
          </h2>
          <p className="text-rn-text-soft-dk mb-8 max-w-xl mx-auto">
            Teste grátis por 30 dias, sem cartão de crédito. Novos comandos
            chegam direto pela atualização automática do plugin.
          </p>
          <Link to="/signup">
            <Button size="lg">Criar conta grátis</Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CommandsPage;
